import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getUnixTime } from 'date-fns';
import { detect } from 'detect-browser';
import { CookieService } from 'ngx-cookie';
import { Observable } from 'rxjs';
import { filter, map, skip, switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv8 } from 'uuid';

import { LiabilitySource } from '../../billers/models/model';
import { AuthState } from '../../shared/models/auth/auth-state.model';
import { AuthStoreService } from '../../shared/services/auth/auth-store.service';
import { IAnalyticsConfig } from '../interfaces/app-config.interface';
import {
  REFERRAL_CODE,
  REFERRER,
  SESSION_ID,
  UTM_CAMPAIGN,
  UTM_CONTENT,
  UTM_MEDIUM,
  UTM_SOURCE,
  UTM_TERM,
} from '../models/cookies.model';
import { AppConfig } from './app-config.service';
import { HandsetService } from './handset.service';
import { SensitiveDataSanitizer } from '../utils/sensitive-data-sanitizer';
import { CrispService } from './crisp.service';
import { MediaObserver } from 'ng-flex-layout';

const browser = detect();
const HUAWEI_OFFER_URL = 'https://go.they.pl/aff_l?offer_id=2945';
const INSTALL_LIBRARY_DELAY_MILLISECONDS = 5000;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  public sessionId: string;
  public analyticsConfig: IAnalyticsConfig;
  public onNavigation: Observable<NavigationEnd>;
  private sanitizer = new SensitiveDataSanitizer();

  constructor(
    private media: MediaObserver,
    private cookies: CookieService,
    private router: Router,
    private authService: AuthStoreService,
    public httpClient: HttpClient,
    public appConfig: AppConfig,
    private handsetService: HandsetService,
    private crispService: CrispService,
  ) {
    this.sessionId = this.cookies.get(SESSION_ID);
    this.analyticsConfig = this.appConfig.config.analytics;
    this.onNavigation = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => e as NavigationEnd),
    );

    // this.extractUTM();
    // this.install();
    // this.registerPageViewsTracking();
    // this.registerSessionClear();
    // this.setGAUserId();
    // this.installWpPixel();
  }

  public clearSession(): void {
    this.sessionId = null;
    this.cookies.remove('sessionId');
    this.hardResetIntercom();
    this.crispService.installCrisp(true);
  }

  public trackLogin(): void {
    return;
    //todo
    const event = new AnalyticsEvent('Logged in');
    event.newSession = true;

    this.startNewSession();
    this.trackEvent(event);
    this.setGAUserId();
    this.crispService.resetCrispSession();
    this.crispService.setCrispAttributes();
  }

  public trackPayment(totalAmount: number): void {
    if (this.analyticsConfig.facebookPixel.enabled) {
      (window as any).fbq('track', 'Purchase', {
        currency: 'PLN',
        value: totalAmount,
      });
    }
  }

  public trackBillerAdded(billerName: string, billerCode: string): void {
    this.trackEvent(
      new AnalyticsEvent('Addcustomerbiller', {
        customerBillerId: billerName,
        id: billerCode,
      }),
    );
  }

  public trackBillerDeleted(billerName: string, billerCode: string): void {
    this.trackEvent(
      new AnalyticsEvent('Deletecustomerbiller', {
        customerBillerId: billerName,
        id: billerCode,
      }),
    );
  }

  public trackCustomerBillerConfigured(liabilitySource: LiabilitySource, billerCode: string): void {
    this.trackEvent(new AnalyticsEvent('Biller Configured',
      `biller=${billerCode} source=${liabilitySource}`));
  }

  public startNewSession(): void {
    this.sessionId = `${uuidv8()}_${getUnixTime(new Date())}`;
    this.cookies.put('sessionId', this.sessionId);
  }


  public trackEvent(event: AnalyticsEvent, lead: boolean = false): void {
    lead ? this.trackGtmCustomLeadEvent(event) : this.trackGtmCustomEvent(event);
    this.trackFacebookCustomEvent(event);
    this.trackEventCollectorEvent(event);
  }

  private trackEventCollectorEvent(event: AnalyticsEvent): void {
    try {
      event.email = this.authService.userInfo && this.authService.userInfo.email;
      event.userName = this.authService.userInfo && this.authService.userInfo.name;
      event.uid = this.cookies.get('intercom-id-' + this.analyticsConfig.intercom.appId);
      event.sessionId = this.sessionId;
      event.utm = this.getUTMfromCookies().getNotEmpty();
      event.userId = this.authService.userInfo && this.authService.userInfo.sub;
      event.customerId = this.authService.userInfo && this.authService.userInfo.customer_id;
      event.client.screenSize = this.getMediaAlias();

      this.httpClient.post(this.appConfig.config.eventCollectorUrl, event).subscribe(
        () => {
        },
        (e) => console.error('Event collector subscribe error ', e),
      );
    } catch (error) {
      console.error(error);
    }
  }

  public trackGtmFormEvent(fieldName: string, formName: string, eventCategory = 'Click form field'): void {
    if (this.analyticsConfig.ga.enabled) {
      const GAEvent = {
        GAEventAction: formName,
        GAEventCategory: eventCategory,
        GAEventLabel: fieldName,
        event: 'GAEvent',
        userId: this.authService.userInfo?.sub,
      };
      this.trackGtm(GAEvent);
    }
  }

  private setGAUserId(): void {
    if (this.authService.userInfo) {
      (window as any).ga('set', 'userId', this.authService.userInfo.sub);
    }
  }

  private trackGtmCustomEvent(event: AnalyticsEvent): void {
    const GAEvent = {
      GAEventAction: event.payload,
      GAEventCategory: event.name,
      GAEventLabel: '',
      event: 'GAEvent',
      userId: event.userId || this.authService.userInfo?.sub,
      os: browser.os,
      clientAppType: this.getClientAppType(),
    };
    this.trackGtm(GAEvent);
  }

  private trackGtmCustomLeadEvent(event: AnalyticsEvent): void {
    if (this.analyticsConfig.ga.enabled) {
      const GAEvent = {
        GAEventAction: event.payload,
        GAEventCategory: event.name,
        GAEventLabel: '',
        event: 'GAEvent',
        lead: 'true',
        userId: event.userId || this.authService.userInfo?.sub,
        os: browser.os,
        clientAppType: this.getClientAppType(),
      };
      this.trackGtm(GAEvent);
    }
  }

  private getClientAppType(): ClientAppType {
    return localStorage.getItem('mobile_app') === 'true' ? ClientAppType.MOBILE : ClientAppType.WEB;
  }

  private trackFacebookCustomEvent(event: AnalyticsEvent): void {
    if (this.analyticsConfig.facebookPixel.enabled) {
      const payload = { ...event.payload };
      if (payload?.email) {
        delete payload.email; // https://www.facebook.com/business/help/611774685654668?id=1205376682832142
      }

      // https://developers.facebook.com/docs/facebook-pixel/advanced/advanced-matching
      payload.em = event.email = this.authService.userInfo && this.authService.userInfo.email;

      (window as any).fbq('trackCustom', event.name, payload);
    }
  }

  public trackRegistrationEventWithGtmIfEnabled(customEvent: any): void {
    if (this.analyticsConfig.ga.enabled) {
      this.trackGtm(customEvent);

      const event = new AnalyticsEvent(customEvent.GAEventCategory, { referral: customEvent.referral });
      this.trackFacebookCustomEvent(event);
      this.trackEventCollectorEvent(event);
      this.addHuaweiAdsScript();
    }
  }

  public addHuaweiAdsScript() {
    var img = document.createElement('img');
    img.setAttribute('src', HUAWEI_OFFER_URL);
    img.setAttribute('width', '1');
    img.setAttribute('height', '1');
    document.body.appendChild(img);
  }

  public trackWpEventIfEnabled(contentName: string): void {
    if (this.analyticsConfig.wpPixel.enabled) {
      (window as any).wph('track', 'ViewContent', { content_type: 'Site', content_name: contentName });
    }
  }

  private installWpPixel(): void {
    if (this.analyticsConfig.wpPixel.enabled) {
      (window as any).wph('init', this.analyticsConfig.wpPixel.id);
    }
  }

  private trackGtm(event: any): void {
    try {
      this.analyticsConfig.gtm.enabled ? (window as any).dataLayer.push(event) : console.log(event);
    } catch (e) {
      console.error(e);
    }
  }

  private install(): void {
    // this.installGA();
    // this.installGTM();
    this.installIntercomWithDelay();
    // this.installDataDogRUM();
    this.crispService.installCrisp();
  }

  private async installIntercomWithDelay(): Promise<void> {
    setTimeout(() => {
      this.installIntercom();
    }, INSTALL_LIBRARY_DELAY_MILLISECONDS);
  }

  private installIntercom(): void {
    if (this.analyticsConfig.intercom.enabled) {
      // installIntercom(this.analyticsConfig.intercom.appId);
      if (!this.appConfig.config.demo) {
        this.registerAuthenticationStateListenerForIntercom();
      }
      this.bootIntercom();
      this.setIntercomUTMs();

      this.handsetService.handsetChanges.subscribe((isHandset) => {
        window.intercomSettings.hide_default_launcher = isHandset;
        Intercom('update');
      });
      if (this.authService.state === AuthState.NOT_AUTHENTICATED && !this.authService.userInfo?.name) {
        this.hardResetIntercom();
      }
    }
  }

  private registerAuthenticationStateListenerForIntercom(): void {
    this.authService.state$
      .pipe(
        filter((state: AuthState) => state === AuthState.AUTHENTICATED),
        switchMap(() =>
          this.httpClient.get<any>(`${this.appConfig.config.apiUrl}/analytics/intercom/customAttributes`),
        ),
        tap((response: any) => this.updateIntercomSettings(response)),
      )
      .subscribe();

    this.authService.state$
      .pipe(
        skip(1),
        filter((state: AuthState) => state === AuthState.NOT_AUTHENTICATED),
        tap(() => console.log('Shutting intercom down')),
        tap(() => Intercom('shutdown')),
        tap(() => (window.intercomSettings = {})),
        tap(() => this.bootIntercom()),
      )
      .subscribe();
  }

  private hardResetIntercom() {
    if (window.intercomSettings) {
      this.shutdownIntercom();
      this.bootIntercom();
    }
  }

  private shutdownIntercom() {
    Intercom('shutdown');
    window.intercomSettings = {};
  }

  private bootIntercom(): void {
    Intercom('boot', {
      app_id: this.analyticsConfig.intercom.appId,
    });
  }

  private setIntercomUTMs(): void {
    // const utm: UTM = this.getUTMfromCookies();
    // window.intercomSettings = {
    //   ...window.intercomSettings,
    //   ...utm.getNotEmpty(),
    // };
    // Intercom('update');
  }

  private extractUTM(): void {
    const utm = this.getUTMsFromURL();
    this.saveUTMtoCookies(utm);
  }

  private getUTMsFromURL(): UTM {
    const queryParam = this.getQueryParams();

    return new UTM(
      queryParam.utm_source,
      queryParam.utm_medium,
      queryParam.utm_campaign,
      queryParam.utm_term,
      queryParam.utm_content,
      document.referrer,
      queryParam.referral_code,
    );
  }

  private getQueryParams(): any {
    const { search } = window.location;
    if (!search) {
      return {};
    }

    const queryParams = {};
    const segments = search.substr(1).split('&');
    segments.forEach((segment) => {
      const [key, value] = segment.split('=');
      if (!queryParams[key]) {
        queryParams[key] = value;
      } else if (queryParams[key] instanceof Array) {
        queryParams[key].push(value);
      } else {
        queryParams[key] = [queryParams[key], value];
      }
    });
    return queryParams;
  }

  private saveUTMtoCookies(utm: UTM): void {
    this.cookies.put(UTM_SOURCE, utm.utmSource);
    this.cookies.put(UTM_MEDIUM, utm.utmMedium);
    this.cookies.put(UTM_TERM, utm.utmTerm);
    this.cookies.put(UTM_CAMPAIGN, utm.utmCampaign);
    this.cookies.put(UTM_CONTENT, utm.utmContent);
    this.cookies.put(REFERRER, utm.referrer);
    this.cookies.put(REFERRAL_CODE, utm.referralCode);
  }

  private registerPageViewsTracking(): void {
    this.onNavigation
      .pipe(
        tap((e: NavigationEnd) => {
          this.trackEvent(new AnalyticsEvent('Page View'));
          this.trackWpEventIfEnabled('View');

          if (this.analyticsConfig.gtm.enabled) {
            const pageViewEvent = {
              event: 'pageview',
              page: { path: this.sanitizer.sanitizeUrl(e.urlAfterRedirects) },
            };
            if (this.authService.userInfo && this.authService.userInfo.sub) {
              (window as any).dataLayer.push({ ...pageViewEvent, userId: this.authService.userInfo.sub });
            } else {
              (window as any).dataLayer.push(pageViewEvent);
            }
          }
        }),
      )
      .subscribe();
  }

  private registerSessionClear(): void {
    this.authService.state$
      .pipe(
        filter((state: AuthState) => state === AuthState.NOT_AUTHENTICATED),
        tap(() => this.clearSession()),
      )
      .subscribe();
  }

  private updateIntercomSettings(customAttributes: any): void {
    window.intercomSettings = {
      ...window.intercomSettings,
      app_id: this.analyticsConfig.intercom.appId,
      email: this.authService.userInfo.email,
      hide_default_launcher: this.handsetService.isHandset,
      name: this.authService.userInfo.name,
      user_id: this.authService.userInfo.customer_id,
      ...customAttributes,
    };
    Intercom('update');
  }

  private getUTMfromCookies(): UTM {
    return new UTM(
      this.cookies.get(UTM_SOURCE),
      this.cookies.get(UTM_MEDIUM),
      this.cookies.get(UTM_CAMPAIGN),
      this.cookies.get(UTM_TERM),
      this.cookies.get(UTM_CONTENT),
      this.cookies.get(REFERRAL_CODE),
    );
  }

  private getMediaAlias(): ScreenSize {
    if (this.media.isActive('xs')) {
      return ScreenSize.xs;
    } else if (this.media.isActive('sm')) {
      return ScreenSize.sm;
    } else if (this.media.isActive('md')) {
      return ScreenSize.md;
    } else if (this.media.isActive('lg')) {
      return ScreenSize.lg;
    } else if (this.media.isActive('xl')) {
      return ScreenSize.xl;
    }
  }

}

export class AnalyticsEvent {
  public name: string;
  public payload: any;
  public uid?: string;
  public customerId?: string;
  public userName?: string;
  public email?: string;
  public userId?: string;
  public sessionId: string;
  public newSession: boolean;
  public tag?: string;
  public source: EventSource;
  public utm: Partial<UTM>;
  public metadata?: Partial<Metadata>;
  public client?: Partial<Client>;
  public serverTime?: Date;
  public clientAppType?: ClientAppType;

  constructor(name: string, payload: any = {}, tag: string = '', clientAppType: ClientAppType = ClientAppType.WEB) {
    this.name = name;
    this.newSession = false;
    this.payload = payload;
    this.source = EventSource.browser;
    this.clientAppType = localStorage.getItem('mobile_app') ? ClientAppType.MOBILE : clientAppType;
    this.client = new Client();
    this.serverTime = new Date();
    this.tag = tag;
    this.metadata = new Metadata();
  }
}

export class UTM {
  public utmSource: string;
  public utmMedium: string;
  public utmCampaign: string;
  public utmTerm: string;
  public utmContent: string;
  public referrer: string;
  public referralCode: string;

  constructor(
    utmSource: string = '',
    utmMedium: string = '',
    utmCampaign: string = '',
    utmTerm: string = '',
    utmContent: string = '',
    referrer: string = '',
    referralCode: string = '',
  ) {
    this.utmSource = utmSource;
    this.utmMedium = utmMedium;
    this.utmCampaign = utmCampaign;
    this.utmTerm = utmTerm;
    this.utmContent = utmContent;
    this.referrer = referrer;
    this.referralCode = referralCode;
  }

  public getNotEmpty(): Partial<UTM> {
    const notEmpty = {};
    for (const [key, value] of Object.entries(this)) {
      if (value) {
        notEmpty[key] = value;
      }
    }
    return notEmpty;
  }
}

export class Client {
  public userAgent: string;
  public browser: string;
  public browserVersion: string;
  public os: string;
  public screenSize: ScreenSize;
  public clientTime: Date;

  constructor() {
    this.userAgent = navigator.userAgent;
    this.browser = browser.name;
    this.browserVersion = browser.version;
    this.os = browser.os.toString();
    this.clientTime = new Date();
  }
}

export class Metadata {
  public href: string;
  public pathname: string;
  public search: string;
  public referrer: string;

  constructor() {
    this.referrer = document.referrer;
    this.pathname = window.location.pathname;
    this.href = window.location.href;
    this.search = window.location.search;
  }
}

enum ScreenSize {
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
}

enum EventSource {
  'browser',
  'scraper',
  'backend',
}

export enum ClientAppType {
  MOBILE = 'mobile_app',
  WEB = 'web_app',
}
